import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useCart } from "@/contexts/CartContext";
import { BURGER_TOPPINGS, formatPrice } from "@/lib/menuData";
import { BURGER_TOPPING_IMAGES } from "@/lib/foodImages";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CHEESE_SLICE_PRICE = 0.50;

// Phase 14: Combo tiers — replaces the single "Make it a Meal" toggle.
// Prices are pulled from real side/drink menu items where possible; otherwise fall
// back to these sensible defaults so the tiers always render.
const COMBO_TIERS = [
  { id: "combo",       label: "Combo",       sub: "Small fries + 24oz soda", fallbackPrice: 2.50 },
  { id: "large_combo", label: "Large Combo", sub: "Large fries + 32oz soda", fallbackPrice: 3.99 },
];

export default function BurgerCustomizer({ open, onClose, menuItems, preSelectedBurger, availableSides, conceptId = "burgers" }) {
  const [selectedBurger, setSelectedBurger] = useState(null);
  const [everything, setEverything] = useState(true);
  const [selectedToppings, setSelectedToppings] = useState([...BURGER_TOPPINGS]);
  const [addBacon, setAddBacon] = useState(false);
  const [addJalapeno, setAddJalapeno] = useState(false);
  const [addCheeseSlice, setAddCheeseSlice] = useState(false);
  const [extraPatties, setExtraPatties] = useState(0);
  const [comboTier, setComboTier] = useState(null); // null | "combo" | "large_combo"
  const { addItem } = useCart();

  useEffect(() => {
    if (open && preSelectedBurger) setSelectedBurger(preSelectedBurger);
  }, [open, preSelectedBurger]);

  const burgerOptions = useMemo(() =>
    (menuItems || []).filter((i) => i.category === "hamburger" || i.category === "cheeseburger"),
  [menuItems]);

  const baconPrice = useMemo(() => {
    const a = (menuItems || []).find((i) => i.id === "burger_bacon");
    return a ? a.price : 1.99;
  }, [menuItems]);

  const pattyPrice = useMemo(() => {
    const a = (menuItems || []).find((i) => i.id === "burger_extra_patty");
    return a ? a.price : 1.99;
  }, [menuItems]);

  // Real-menu-item lookups for combo pricing (fall back to the tier defaults).
  const comboPrices = useMemo(() => {
    const items = menuItems || [];
    const findPrice = (id, fallback) => (items.find((i) => i.id === id)?.price ?? fallback);
    return {
      combo: findPrice("burger_combo_small", COMBO_TIERS[0].fallbackPrice),
      large_combo: findPrice("burger_combo_large", COMBO_TIERS[1].fallbackPrice),
    };
  }, [menuItems]);

  const reset = () => {
    setSelectedBurger(null); 
    setEverything(true); 
    setSelectedToppings([...BURGER_TOPPINGS]);
    setAddBacon(false); 
    setAddJalapeno(false);
    setAddCheeseSlice(false);
    setExtraPatties(0); 
    setComboTier(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const toggleTopping = (t) => {
    setSelectedToppings((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
    setEverything(false);
  };

  const handleEverythingToggle = (checked) => {
    setEverything(checked);
    setSelectedToppings(checked ? [...BURGER_TOPPINGS] : []);
  };

  const totalPrice = () => {
    if (!selectedBurger) return 0;
    let t = selectedBurger.price;
    if (addBacon) t += baconPrice;
    if (addJalapeno) t += 0; // free
    if (addCheeseSlice) t += CHEESE_SLICE_PRICE;
    t += extraPatties * pattyPrice;
    if (comboTier) t += comboPrices[comboTier];
    return t;
  };

  const canSubmit = !!selectedBurger;

  const handleConfirm = () => {
    if (!canSubmit) return;
    const toppingStr = everything ? "Everything" : selectedToppings.length > 0 ? selectedToppings.join(", ") : "Plain";
    const customs = { toppings: toppingStr };
    if (addBacon) customs.bacon = `Yes (+${formatPrice(baconPrice)})`;
    if (addJalapeno) customs.jalapeno = "Yes (Free)";
    if (addCheeseSlice) customs.cheese_slice = `Yes (+${formatPrice(CHEESE_SLICE_PRICE)})`;
    if (extraPatties > 0) customs.extra_patties = `${extraPatties}x (+${formatPrice(extraPatties * pattyPrice)})`;
    if (comboTier) {
      const tier = COMBO_TIERS.find((t) => t.id === comboTier);
      customs.combo = `${tier.label} — ${tier.sub} (+${formatPrice(comboPrices[comboTier])})`;
      customs.soda_flavor = "Selected at pickup";
    }

    let name = selectedBurger.name;
    if (comboTier) {
      const tier = COMBO_TIERS.find((t) => t.id === comboTier);
      name += ` (${tier.label})`;
    }

    addItem({
      name, concept: conceptId, category: selectedBurger.category,
      price: totalPrice(), quantity: 1, customizations: customs,
    });
    toast.success(`${name} added to cart`);
    handleClose();
  };

  const showBurgerSelection = !preSelectedBurger;
  // `availableSides` is intentionally unused now — combo tiers ship with fixed
  // side/drink sizes chosen at pickup. Retained on the props signature for
  // backward compatibility with existing callers.
  void availableSides;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle style={{ fontFamily: "Manrope" }} className="text-lg">
            {preSelectedBurger ? `Customize: ${preSelectedBurger.name}` : "Build Your Burger"}
          </DialogTitle>
          <DialogDescription>
            {preSelectedBurger ? `${formatPrice(preSelectedBurger.price)} base` : "Choose size, toppings & add-ons"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          {showBurgerSelection && (
            <>
              <h4 className="font-bold text-sm text-slate-900 mb-2 mt-2" style={{ fontFamily: "Manrope" }}>Choose Burger *</h4>
              <div className="space-y-2">
                {burgerOptions.map((b) => (
                  <button key={b.id} data-testid={`burger-option-${b.id}`}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between ${selectedBurger?.id === b.id ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                    onClick={() => setSelectedBurger(b)}>
                    <p className="font-semibold text-sm text-slate-900">{b.name}</p>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ color: "#F59E0B" }}>{formatPrice(b.price)}</span>
                      {selectedBurger?.id === b.id && <Check className="h-4 w-4 text-amber-500" />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          <h4 className="font-bold text-sm text-slate-900 mb-2 mt-4" style={{ fontFamily: "Manrope" }}>Toppings</h4>
          
          {/* Quick Tip */}
          <div className="mb-3 p-3 rounded-xl flex items-start gap-2" style={{ backgroundColor: "#E0F2FE", border: "1px solid #7DD3FC" }}>
            <span className="text-lg">💡</span>
            <p className="text-sm" style={{ color: "#0369A1" }}>
              <strong>Tip:</strong> Un-toggle to customize your burger toppings
            </p>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50 border border-amber-200 mb-2">
            <span className="text-sm font-semibold text-slate-900">Everything</span>
            <Switch data-testid="everything-toggle" checked={everything} onCheckedChange={handleEverythingToggle} />
          </div>
          {!everything && (
            <div className="space-y-1.5 animate-fade-in">
              {BURGER_TOPPINGS.map((t) => (
                <label key={t} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                  {BURGER_TOPPING_IMAGES[t] ? (
                    <img
                      src={BURGER_TOPPING_IMAGES[t]}
                      alt={t}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-slate-200"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex-shrink-0 border-2 border-slate-200" />
                  )}
                  <Checkbox checked={selectedToppings.includes(t)} onCheckedChange={() => toggleTopping(t)} />
                  <span className="text-sm font-medium text-slate-700">{t}</span>
                </label>
              ))}
            </div>
          )}

          <h4 className="font-bold text-sm text-slate-900 mb-2 mt-4" style={{ fontFamily: "Manrope" }}>Add-ons</h4>
          
          {/* Add Bacon */}
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between mb-2">
            <div><p className="font-bold text-sm text-slate-900">Add Bacon</p><p className="text-xs text-slate-500">+{formatPrice(baconPrice)}</p></div>
            <Switch data-testid="bacon-toggle" checked={addBacon} onCheckedChange={setAddBacon} />
          </div>

          {/* Jalapeño Pepper - Free */}
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between mb-2">
            <div>
              <p className="font-bold text-sm text-slate-900">Jalapeño Pepper</p>
              <p className="text-xs text-slate-500">Free</p>
            </div>
            <Switch 
              data-testid="jalapeno-toggle" 
              checked={addJalapeno} 
              onCheckedChange={setAddJalapeno} 
            />
          </div>

          {/* Cheese Slice - $0.50 */}
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between mb-2">
            <div>
              <p className="font-bold text-sm text-slate-900">Cheese Slice</p>
              <p className="text-xs text-slate-500">+{formatPrice(CHEESE_SLICE_PRICE)}</p>
            </div>
            <Switch 
              data-testid="cheese-slice-toggle" 
              checked={addCheeseSlice} 
              onCheckedChange={setAddCheeseSlice} 
            />
          </div>

          {/* Extra Patty */}
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between mb-2">
            <div><p className="font-bold text-sm text-slate-900">Extra Patty</p><p className="text-xs text-slate-500">{formatPrice(pattyPrice)} each</p></div>
            <div className="flex items-center gap-2">
              <button data-testid="patty-minus" className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center" onClick={() => setExtraPatties(Math.max(0, extraPatties - 1))}>-</button>
              <span className="text-sm font-bold min-w-[16px] text-center">{extraPatties}</span>
              <button data-testid="patty-plus" className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center" onClick={() => setExtraPatties(extraPatties + 1)}>+</button>
            </div>
          </div>

          {/* Combo Tier Selection (Phase 14 — replaces the old Make it a Meal toggle) */}
          <div className="mt-4 p-3 rounded-xl bg-amber-50 border-2 border-dashed border-amber-400">
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-sm text-slate-900" style={{ fontFamily: "Manrope" }}>Make it a Combo</p>
              {comboTier && (
                <button
                  data-testid="combo-clear"
                  onClick={() => setComboTier(null)}
                  className="text-[11px] underline text-slate-500"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {COMBO_TIERS.map((tier) => {
                const active = comboTier === tier.id;
                return (
                  <button
                    key={tier.id}
                    data-testid={`combo-${tier.id}`}
                    onClick={() => setComboTier(tier.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${active ? "border-amber-500 bg-amber-100" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-bold text-sm text-slate-900">{tier.label}</p>
                      {active && <Check className="h-4 w-4 text-amber-500" />}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{tier.sub}</p>
                    <p className="text-xs font-bold mt-1" style={{ color: "#F59E0B" }}>+{formatPrice(comboPrices[tier.id])}</p>
                  </button>
                );
              })}
            </div>
            {comboTier && (
              <p className="text-[10px] text-slate-500 mt-2 italic">Soda flavor selected at pickup</p>
            )}
          </div>
        </div>

        <div className="px-6 pb-6 pt-2 border-t border-slate-100">
          <Button data-testid="burger-confirm-btn" className="w-full h-12 rounded-xl font-bold" style={{ backgroundColor: "#F59E0B" }}
            disabled={!canSubmit} onClick={handleConfirm}>
            Add to Cart {totalPrice() > 0 && `- ${formatPrice(totalPrice())}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
